import { loadScript } from "@/functions/misc";
import { isIOS } from "@vueuse/core";

declare const pdfjsLib: any;

export const usePDFStore = defineStore("pdf", () => {
  const debug = useDebug();
  const toast = useToastStore();
  const { t } = useI18n();

  async function loadPDFLib() {
    await loadScript(
      "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.4.456/build/pdf.min.js",
      "pdfjslib",
    );
  }

  function createCanvas(container: HTMLDivElement) {
    const canvas = document.createElement("canvas") as HTMLCanvasElement;
    canvas.id = `canvas-page`;
    container.appendChild(canvas);
    return canvas;
  }

  function createCanvasContainer() {
    const container = document.createElement("div") as HTMLDivElement;
    document.body.appendChild(container);
    return container;
  }

  async function getPDFDoc(url: string) {
    await loadPDFLib();
    const task = pdfjsLib.getDocument(url);
    const pdfDoc = await task.promise;
    const numPages = pdfDoc.numPages;
    return {
      pdfDoc,
      numPages,
    };
  }

  async function generatePage(
    pdfdoc: any,
    pageNumber: number,
    container: HTMLDivElement,
  ) {
    const page = await pdfdoc.getPage(pageNumber);
    const canvas = createCanvas(container);

    const context = canvas.getContext("2d", {
      willReadFrequently: true,
      alpha: false,
    });

    if (!context) {
      throw new Error("Context can be generated from Canvas");
    }
    const viewport = page.getViewport({ scale: 2 });

    canvas.height = viewport.height;
    canvas.width = viewport.width;

    const pageRendering = page.render({
      canvasContext: context,
      undefined,
      viewport,
    });

    await pageRendering.promise;

    const image = canvas.toDataURL("image/png");
    canvas.remove();
    return image;
  }

  async function convertPDFToPNGs(pdfUrl: string) {
    const { pdfDoc, numPages } = await getPDFDoc(pdfUrl);

    const container = createCanvasContainer();
    const imagePromises = [] as Promise<string>[];
    for (let page = 1; page <= numPages; page++) {
      imagePromises.push(generatePage(pdfDoc, page, container));
    }

    const images = await Promise.all(imagePromises);
    container.remove();
    return images;
  }

  async function printPdf(url: string) {
    debug.log("url", url);
    if (isFirefox() || (isIOS && !isSafari(true)) || isAndroid()) {
      //  throw new Error('Firefox printing of PDFs is not supported"');
      printUsingNewWindow(window.location.origin + url);
      return;
    }

    let iframe = document.getElementById("PDFIFrame") as HTMLIFrameElement;

    if (!iframe) {
      iframe = document.createElement("iframe");
      iframe.id = "PDFIFrame";
      iframe.style.display = "none";
    }

    try {
      await new Promise<void>((res, rej) => {
        iframe.onload = () => {
          setTimeout(() => {
            iframe.focus();
            // notes: this does not work well for chrome on iOS.
            // found issue with potential same site origin security issues as well doing this..
            // solution: disable printing feature for iOS devices that are anything OTHER than safari
            if (!iframe.contentWindow) {
              rej("No content window available");
              return;
            }
            try {
              iframe.contentWindow.print();
              URL.revokeObjectURL(url);
              res();
            } catch (err) {
              rej(err);
            }
          }, 1);
        };

        iframe.src = window.location.origin + url;
        document.body.appendChild(iframe);
      });
    } catch (err) {
      printUsingNewWindow(window.location.origin + url);
    }
  }

  function printUsingNewWindow(url: string) {
    const printWindow = window.open(url);
    if (!printWindow) {
      toast.warningToast(t("SV.MES_EnablePrinting"));
      throw new Error("Cannot print");
    }

    printWindow.onload = () => {
      printWindow.print();
    };

    //Close window once print is finished
    printWindow.onafterprint = function () {
      printWindow.close();
    };
  }

  return {
    printPdf,
    convertPDFToPNGs,
  };
});
